import React, { useEffect, useMemo } from 'react';
import { useConstructorBrandById } from 'common/api/constructor/useConstructorBrandById';
import { useRootSelector } from 'common/features/featuresReducer';
import { DealUIObject, DealUIObjectWithBrand } from 'common/api/deals/types';
import { getDealsFromAffiliateUrl } from 'common/api/deals/getDealsByLinkId';
import {
    selectDealByBrandId,
    setDealsByBrandId,
    selectWalletAdByBrandId,
} from 'common/features/store/duck/wallet-ads/duck';
import { useDispatch } from 'react-redux';
import ConstructorIO from '@constructor-io/constructorio-client-javascript';

interface MinimalBrand {
    id: number | string;
}

export function useGetAndCacheTopDealForBrand(
    constructorClient: ConstructorIO,
    cardBrand: MinimalBrand,
    token: string,
    isFlipgiveEnabled: boolean | undefined,
    isConstructorAffiliateEnabled: boolean | undefined
) {
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState<Error | null>(null);

    const cachedDeal = useRootSelector(selectDealByBrandId)(cardBrand?.id);
    const walletAdSelector = useRootSelector(selectWalletAdByBrandId);
    const dispatch = useDispatch();

    const constructorBrand = useConstructorBrandById(
        isConstructorAffiliateEnabled,
        constructorClient,
        cardBrand?.id
    );

    const affiliateUrl = useMemo(() => {
        return isFlipgiveEnabled
            ? constructorBrand?.constructorBrand?.affiliateOfferUrl
            : undefined;
    }, [isFlipgiveEnabled, constructorBrand]);

    // Wait for constructorBrand to finish loading before attempting to fetch deals
    const shouldAttemptFetch =
        constructorBrand.constructorBrandHasLoaded || !isConstructorAffiliateEnabled;
    const hasFetchInputs = !!affiliateUrl && !!token && !!cardBrand?.id;

    // Reset loading state when brand changes
    useEffect(() => {
        setIsLoading(true);
        setError(null);
    }, [cardBrand?.id]);

    // Handle timeout if constructorBrand takes too long to load
    useEffect(() => {
        // If we have a cached deal (truthy) or it's null (fetched with no results), don't wait
        if (cachedDeal !== undefined) {
            return;
        }

        if (shouldAttemptFetch) {
            return;
        }

        // If constructorBrand is still loading, set a timeout to stop waiting
        const timeoutId = setTimeout(() => {
            setIsLoading(false);
        }, 5000); // Wait 5 seconds for constructorBrand to load

        return () => {
            clearTimeout(timeoutId);
        };
    }, [cachedDeal, shouldAttemptFetch]);

    useEffect(() => {
        // If we have a cached deal (truthy), use it and stop loading
        if (cachedDeal) {
            setIsLoading(false);
            return;
        }

        // If cachedDeal is null, we've already fetched and got no results - stop loading
        if (cachedDeal === null) {
            setIsLoading(false);
            return;
        }

        // cachedDeal is undefined - deal not fetched yet, continue to fetch logic

        // If constructorBrand is still loading, wait for it to finish
        if (!shouldAttemptFetch) {
            return;
        }

        if (!hasFetchInputs) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        let timeoutId: NodeJS.Timeout | null = null;
        let isCancelled = false;

        // Set a timeout to prevent infinite loading (10 seconds)
        timeoutId = setTimeout(() => {
            if (!isCancelled) {
                isCancelled = true;
                setIsLoading(false);
                dispatch(setDealsByBrandId({ [cardBrand?.id]: null }));
            }
        }, 10000);

        getDealsFromAffiliateUrl(affiliateUrl, token)
            .then((response: DealUIObject[]) => {
                if (isCancelled) {
                    return;
                }

                if (timeoutId) {
                    clearTimeout(timeoutId);
                }

                // Check if we actually got any deals back
                if (response.length === 0) {
                    dispatch(setDealsByBrandId({ [cardBrand?.id]: null }));
                    setIsLoading(false);
                    return;
                }

                const dealWithBrand: DealUIObjectWithBrand = {
                    ...response[0],
                    brand: constructorBrand.constructorBrand,
                };

                // Check if wallet ad has promotionalText - if so, override deal title and type
                const walletAdConfig = walletAdSelector(cardBrand?.id);
                const promotionalText = walletAdConfig?.promotionalText;

                let finalDeal: DealUIObjectWithBrand = dealWithBrand;
                if (promotionalText) {
                    // Override title with promotionalText and force dealType to 'deal'
                    finalDeal = {
                        ...dealWithBrand,
                        title: promotionalText,
                        dealType: 'deal' as const,
                    };
                }

                dispatch(setDealsByBrandId({ [cardBrand?.id]: finalDeal }));
                setIsLoading(false);
            })
            .catch((error: Error) => {
                if (isCancelled) {
                    return;
                }

                if (timeoutId) {
                    clearTimeout(timeoutId);
                }

                setError(error);
                setIsLoading(false);
            });

        return () => {
            isCancelled = true;
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [
        dispatch,
        cardBrand?.id,
        cachedDeal,
        constructorBrand,
        affiliateUrl,
        token,
        shouldAttemptFetch,
        hasFetchInputs,
    ]);

    // Derived in render: isLoading also drops on the 5 s brand timeout, before any fetch ran.
    const isFetchSkipped = shouldAttemptFetch && !hasFetchInputs;
    const hasSettled = cachedDeal !== undefined || error !== null || isFetchSkipped;

    return {
        deal: cachedDeal as DealUIObjectWithBrand | null,
        isLoading,
        error,
        hasSettled,
    };
}
